import React, { useEffect, useMemo, useState } from 'react';
import { GoogleMap, Marker, useJsApiLoader, Autocomplete } from '@react-google-maps/api';
import PropTypes from 'prop-types';
import { setKey, fromLatLng, fromAddress } from 'react-geocode';
import { TextField } from '@mui/material';
import './map.scss';
import MarkerImg from './pin.png';

function getComponent(components, type) {
  return components.find((c) => c.types.includes(type))?.long_name || '';
}

const Map = ({ onLocationChange, initialLocation }) => {
  const [auto, setAuto] = useState(null); // Autocomplete instance
  const [address, setAddress] = useState('');
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    id: 'google-map-script',
    libraries: ['places'],
  });

  useEffect(() => {
    if (process.env.REACT_APP_GOOGLE_MAPS_API_KEY) {
      setKey(process.env.REACT_APP_GOOGLE_MAPS_API_KEY);
    }
  }, []);

  const DEFAULT_CENTER = { lat: 41.7056448, lng: 44.7870996 };
  const [currentLocation, setCurrentLocation] = useState(initialLocation ?? DEFAULT_CENTER);
  const [loading, setLoading] = useState(true);

  // Reverse-geocode and also keep the address field in sync
  const handleReverseGeocode = async (lat, lng) => {
    try {
      const res = await fromLatLng(lat, lng);
      const result = res?.results?.[0];
      const components = result?.address_components || [];
      const address1 = result?.formatted_address || '';
      const city =
        getComponent(components, 'locality') ||
        getComponent(components, 'administrative_area_level_2') ||
        '';
      const zipCode = getComponent(components, 'postal_code') || '';
      const state = getComponent(components, 'administrative_area_level_1') || '';
      const country = getComponent(components, 'country') || '';

      setAddress(address1);
      onLocationChange?.({ lat, long: lng, city, zipCode, state, country, address1 });
    } catch {
      // ignore geocode errors
    }
  };

  // Initial position
  useEffect(() => {
    let cancelled = false;

    const done = (loc) => {
      if (cancelled) return;
      setCurrentLocation(loc);
      setLoading(false);
      handleReverseGeocode(loc.lat, loc.lng);
    };

    const hasInitial =
      initialLocation &&
      typeof initialLocation.lat === 'number' &&
      typeof initialLocation.lng === 'number' &&
      !Number.isNaN(initialLocation.lat) &&
      !Number.isNaN(initialLocation.lng);

    if (hasInitial) {
      done(initialLocation);
    } else {
      navigator.geolocation.getCurrentPosition(
        ({ coords }) => done({ lat: coords.latitude, lng: coords.longitude }),
        () => done(DEFAULT_CENTER)
      );
    }

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialLocation]);

  // Marker drag -> reverse geocode
  const handleMarkerDragEnd = (e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    setCurrentLocation({ lat, lng });
    handleReverseGeocode(lat, lng);
  };

  // Autocomplete select -> move marker & reverse geocode
  const handlePlaceChanged = () => {
    if (!auto) return;
    const place = auto.getPlace?.();
    if (!place || !place.geometry) return;

    const formatted = place.formatted_address || '';
    setAddress(formatted);

    const loc = place.geometry.location;
    const lat = loc.lat();
    const lng = loc.lng();

    setCurrentLocation({ lat, lng });
    handleReverseGeocode(lat, lng);
  };

  // User types address and presses Enter / blurs -> forward geocode
  const geocodeTypedAddress = async () => {
    const q = address?.trim();
    if (!q) return;
    try {
      const res = await fromAddress(q);
      const result = res?.results?.[0];
      if (!result) return;

      const { lat, lng } = result.geometry.location; // numbers
      setCurrentLocation({ lat, lng });
      setAddress(result.formatted_address || q);
      await handleReverseGeocode(lat, lng);
    } catch {
      // ignore
    }
  };

  const mapContainerStyle = useMemo(
    () => ({ height: '300px', width: '100%', borderRadius: '21.209px' }),
    []
  );

  const markerIcon = useMemo(() => {
    if (!isLoaded || typeof window === 'undefined' || !window.google) {
      return { url: MarkerImg };
    }
    return {
      url: MarkerImg,
      scaledSize: new window.google.maps.Size(63, 63),
    };
  }, [isLoaded]);

  const darkMapStyles = [
    { elementType: 'geometry', stylers: [{ color: '#f5f5f5' }] },
    { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
    { elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
    { elementType: 'labels.text.stroke', stylers: [{ color: '#f5f5f5' }] },
    {
      featureType: 'administrative_land_parcel',
      elementType: 'labels.text.fill',
      stylers: [{ color: '#bdbdbd' }],
    },
    { featureType: 'poi', elementType: 'geometry', stylers: [{ color: '#eeeeee' }] },
    { featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
    { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#e5e5e5' }] },
    { featureType: 'poi.park', elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] },
    { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
    {
      featureType: 'road.arterial',
      elementType: 'labels.text.fill',
      stylers: [{ color: '#757575' }],
    },
    { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#dadada' }] },
    {
      featureType: 'road.highway',
      elementType: 'labels.text.fill',
      stylers: [{ color: '#616161' }],
    },
    { featureType: 'road.local', elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] },
    { featureType: 'transit.line', elementType: 'geometry', stylers: [{ color: '#e5e5e5' }] },
    { featureType: 'transit.station', elementType: 'geometry', stylers: [{ color: '#eeeeee' }] },
    { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#c9c9c9' }] },
    { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] },
  ];

  if (!isLoaded) {
    return (
      <div className="map-container">
        <div className="loading-indicator">
          <div className="circle" />
        </div>
      </div>
    );
  }

  return (
    <div className="map-container">
      {loading ? (
        <div className="loading-indicator">
          <div className="circle" />
        </div>
      ) : (
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          options={{ styles: darkMapStyles, fullscreenControl: false }}
          zoom={15}
          center={currentLocation}
        >
          <Marker
            position={currentLocation}
            icon={markerIcon}
            draggable
            onDragEnd={handleMarkerDragEnd}
          />
        </GoogleMap>
      )}

      <Autocomplete
        onLoad={setAuto}
        onPlaceChanged={handlePlaceChanged}
        options={{ componentRestrictions: { country: ['ge', 'us'] } }}
      >
        <TextField
          variant="filled"
          style={{ padding: '0px 10px 15px 5px', width: '99%', marginTop: '10px' }}
          type="text"
          label="Address"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && geocodeTypedAddress()}
          onBlur={geocodeTypedAddress}
          fullWidth
        />
      </Autocomplete>
    </div>
  );
};

Map.propTypes = {
  onLocationChange: PropTypes.func,
  initialLocation: PropTypes.object,
};

export default Map;
